import React, { useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";

/**
 * Generic TanStack Table component that can be reused across the application
 *
 * @param {Object} props - Component props
 * @param {Array|Object} props.data - The data to display in the table
 * @param {Array} props.columns - Column definitions for the table
 * @param {Object} props.options - Additional table options
 * @param {Boolean} props.showPagination - Whether to show pagination controls
 * @param {String} props.emptyMessage - Message to display when there's no data
 * @param {String} props.className - Additional CSS class for the table container
 * @param {Boolean} props.hasNextPage - Indicates if more pages are available
 * @param {Function} props.onPaginationChange - Callback for pagination changes
 * @param {Number} props.pageIndex - Current page index
 * @param {Number} props.pageSize - Number of rows per page
 * @returns {JSX.Element} The rendered table
 */
const GenericTable = ({
  data,
  getRowProps,
  columns,
  options = {},
  showPagination = true,
  emptyMessage = "No data available",
  className = "",
  hasNextPage = true,
  onPaginationChange,
  pageIndex,
  pageSize,
}) => {
  // Ensure data is an array
  const tableData = useMemo(() => {
    if (!data) return [];
    if (!Array.isArray(data)) return [data];
    return data;
  }, [data]);

  // Create the table instance
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageIndex ?? 0, // Use prop directly
        pageSize: pageSize ?? 10,
      },
    },
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newState = updater({
          pageIndex: pageIndex ?? 0,
          pageSize: pageSize ?? 10,
        });
        onPaginationChange?.(newState);
      } else {
        onPaginationChange?.(updater);
      }
    },
    ...options,
  });

  // If no data is available
  if (tableData.length === 0) {
    return <div className="p-4 text-center text-gray-500">{emptyMessage}</div>;
  }

  return (
    <div className={`h-full ${className}`}>
      <table className="min-w-full border-collapse border border-gray-200">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="bg-white border border-gray-200 px-4 py-2"
                  onClick={header.column.getToggleSortingHandler()}
                  style={{
                    cursor: header.column.getCanSort() ? "pointer" : "default",
                    width: header.column.columnDef.meta?.width || "auto", // ✅ added line
                  }}
                >

                  <div className="text-center font-bold text-gray-700 text-base">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getCanSort() && (
                      <span className="ml-1 text-gray-400">
                        {{
                          asc: "↑",
                          desc: "↓",
                        }[header.column.getIsSorted()] || ""}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
            >
              {row.getVisibleCells().map((cell) => {
                const columnDef = cell.column.columnDef;
                return (
                  <td
                    key={cell.id}
                    className={`border border-gray-200 px-4 py-2 text-base font-['Semantic/Strong'] text-gray-700 ${columnDef.cellClassName || ""}`}
                    style={{
                      cursor: columnDef.onClick ? "pointer" : "default",
                      width: columnDef.meta?.width || "auto", // ✅ added line
                      ...(columnDef.cellStyle || {}),
                    }}
                    onClick={columnDef.onClick ? () => columnDef.onClick({
                      row: row.original,
                      value: cell.getValue(),
                      rowIndex: row.index,
                      columnId: cell.column.id,
                    }) : undefined}
                  >

                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination controls */}
      {showPagination && tableData.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-6 py-3 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                onPaginationChange?.({
                  pageIndex: 0,
                  pageSize: table.getState().pagination.pageSize,
                })
              }
              disabled={pageIndex === 0}
              className="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-150"
              title="First Page"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7m0 0l7-7m-7 7h18M4 12h14" />
              </svg>
            </button>

            <button
              onClick={() =>
                onPaginationChange?.({
                  pageIndex: Math.max(0, pageIndex - 1),
                  pageSize: table.getState().pagination.pageSize,
                })
              }
              disabled={pageIndex === 0}
              className="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-150"
              title="Previous Page"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <span className="text-base font-['Semantic/Strong'] text-gray-700">
              Page {pageIndex + 1}
            </span>

            <button
              onClick={() =>
                onPaginationChange?.({
                  pageIndex: pageIndex + 1,
                  pageSize: table.getState().pagination.pageSize,
                })
              }
              disabled={!hasNextPage}
              className="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-150"
              title="Next Page"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              onClick={() => {
                if (options.totalPages && onPaginationChange) {
                  onPaginationChange({
                    pageIndex: options.totalPages - 1,
                    pageSize: table.getState().pagination.pageSize,
                  });
                }
              }}
              disabled={!hasNextPage || !options.totalPages}
              className="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-150"
              title="Last Page"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Show</span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) =>
                onPaginationChange?.({
                  pageIndex: 0, // reset to first page
                  pageSize: Number(e.target.value),
                })
              }
              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
            >
              {[10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600">entries</span>
          </div>
        </div>


      )}
    </div>
  );
};

export default GenericTable;